/**
 * MasterTypeahead — combobox driven by a search-based master-data hook.
 *
 * Used for Language (`useLanguages`), Issuing country (`useNationalities`),
 * Guardian relationship (`useRelations`). The component is data-source-
 * agnostic — the caller passes the current query string, the result
 * array, and an `isPending` flag. The combobox renders results in a
 * Command list inside a Popover.
 *
 * Flutter parity is loose here: the original implementations used
 * `field_and_label.dart` autocomplete widgets (canvas-rendered, no
 * native focus). Per CLAUDE.md "Flutter-web workaround simplification
 * rule", shadcn Command + Popover is the correct React idiom and gives
 * keyboard + a11y for free.
 */

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { AnchoredPopover, AnchoredPopoverContent, AnchoredPopoverTrigger } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';

export interface MasterTypeaheadItem {
  /** Stable key for the React list. Falls back to `name` if `id` is null. */
  id: string | null;
  /** Display label. */
  name: string | null;
}

export interface MasterTypeaheadProps<T extends MasterTypeaheadItem> {
  value: T | null;
  onSelect: (item: T | null) => void;
  placeholder?: string;
  query: string;
  onQueryChange: (q: string) => void;
  results: T[];
  isPending?: boolean;
  /** Optional minimum length before showing the results list — typeahead
   *  hooks usually don't fire below 2 chars. */
  minQueryLength?: number;
  /** Red border (the form's error colour): the field is asked for and still empty. */
  invalid?: boolean;
}

export function MasterTypeahead<T extends MasterTypeaheadItem>({
  value,
  onSelect,
  placeholder = 'Search…',
  query,
  onQueryChange,
  results,
  isPending = false,
  minQueryLength = 2,
  invalid = false,
}: MasterTypeaheadProps<T>) {
  // PROD-2743 · switch ON: DS `down` / `search` glyphs at the 16px inline step. OFF = today.
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset the local query when the value changes externally (form reset
  // on edit-mode load).
  useEffect(() => {
    if (!open) onQueryChange('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.id]);

  const trimmed = query.trim();
  const showResults = trimmed.length >= minQueryLength;

  return (
    <AnchoredPopover open={open} onOpenChange={setOpen}>
      <AnchoredPopoverTrigger
        aria-invalid={invalid || undefined}
        className={cn('mt-1 w-full inline-flex items-center justify-between h-10 px-3 rounded-md border border-border-default bg-transparent text-sm text-text-primary hover:bg-grey-100 focus:outline-none focus:ring-1 focus:ring-brand-primary', invalid && 'border-status-error')}
        onClick={() => setTimeout(() => inputRef.current?.focus(), 0)}
      >
        <span className={cn('truncate', value?.name ? '' : 'text-text-muted')}>
          {value?.name ?? placeholder}
        </span>
        <ChevronDown className="size-4 text-text-secondary flex-shrink-0 ml-2" />
      </AnchoredPopoverTrigger>
      <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }} className="p-0 w-anchor min-w-65 gap-3">
        <div className="p-2 border-b border-border-subtle">
          <div className="relative">
            <Search className="absolute left-3 top-3 size-4 text-text-hint pointer-events-none" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder={placeholder}
              className="w-full h-10 pl-8 pr-3 rounded-md bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:bg-surface-card focus:outline-none focus:ring-1 focus:ring-brand-primary"
            />
          </div>
        </div>
        <div className="max-h-65 overflow-y-auto scrollbar-hide py-1">
          {value ? (
            <button
              type="button"
              onClick={() => {
                onSelect(null);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-sm text-text-muted hover:bg-grey-100"
            >
              Clear selection
            </button>
          ) : null}
          {!showResults ? (
            <p className="px-3 py-2 text-sm text-text-muted">
              Type {minQueryLength}+ characters to search.
            </p>
          ) : isPending ? (
            <p className="px-3 py-2 text-sm text-text-muted">Searching…</p>
          ) : results.length === 0 ? (
            <p className="px-3 py-2 text-sm text-text-muted">No matches.</p>
          ) : (
            results.map((item, i) => (
              <button
                key={item.id ?? `${item.name}-${i}`}
                type="button"
                onClick={() => {
                  onSelect(item);
                  setOpen(false);
                }}
                className={cn(
                  'w-full text-left px-3 py-2 text-sm hover:bg-grey-100',
                  value?.id && value.id === item.id ? 'bg-blue-100 text-brand-primary' : '',
                )}
              >
                {item.name}
              </button>
            ))
          )}
        </div>
      </AnchoredPopoverContent>
    </AnchoredPopover>
  );
}
